import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { ModalBase } from '@components/Modal';
import { Input } from '@components/Input';
import Button from '@components/Button';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { IntegrationSetupType } from '@constants/integrations';
import { ApiIcon } from 'assets/images/integrations';
import zapier from 'assets/images/integrations/zapier.png';
import ContactSalesButton from '@features/Paywall/ContactSalesButton';
import { integrationRequestsService } from '@services';
import { alertActions } from '@actions';
import { handleApiError } from '@helpers/api';
import { getIntegrationIcon } from '../integrationIcons';
import { SearchWrapper, IntegrationList, IntegrationRow, IntegrationRowLeft, EmptyResults, ChipRow } from '../styles';

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        outline: 'none',
        width: 640,
        maxWidth: 'calc(100% - 32px)',
        height: 640,
        '@media (max-width: 900px)': {
            height: 'var(--100vh)',
            maxWidth: 'none',
            width: '100%',
            borderRadius: 0,
        },
    },
});

const ActionChip = styled(Chip)({
    height: 32,
    borderRadius: 16,
    maxWidth: '100%',
    '& .MuiChip-label': {
        fontFamily: 'Rubik, sans-serif',
        fontSize: 13,
        color: 'var(--text-primary)',
    },
    '& .MuiChip-icon': {
        marginLeft: 10,
    },
});

const AddIntegrationModal = ({ isOpen, onClose, integrations, onConnect }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [search, setSearch] = useState('');

    const term = search.trim();

    const filtered = useMemo(() => {
        const value = term.toLowerCase();
        if (!value) return integrations;
        return integrations.filter(
            item => item.name.toLowerCase().includes(value) || item.category?.toLowerCase().includes(value),
        );
    }, [integrations, term]);

    const onSelect = item => {
        onConnect(item);
        onClose();
    };

    const onSendRequest = async () => {
        if (!term) return;
        try {
            await integrationRequestsService.requestIntegration({ integrationName: term });
            dispatch(alertActions.success(t('SettingsIntegrations.requestIntegrationSuccess')));
            onClose();
        } catch (error) {
            handleApiError(error, 'SettingsIntegrations.requestIntegrationError');
        }
    };

    const getActionText = setupType =>
        setupType === IntegrationSetupType.ApiDocs ? t('buttons.documentation') : t('buttons.connect');

    return (
        <Modal isOpen={isOpen} handleClose={onClose} title={t('SettingsIntegrations.addIntegration')}>
            <SearchWrapper>
                <Input
                    autoFocus
                    label={t('SettingsIntegrations.searchPlaceholder')}
                    inputProps={{ value: search, onChange: e => setSearch(e.target.value) }}
                />
            </SearchWrapper>

            {filtered.length ? (
                <IntegrationList>
                    {filtered.map(item => (
                        <IntegrationRow key={item.id}>
                            <IntegrationRowLeft>
                                <span style={{ display: 'flex' }}>{getIntegrationIcon(item.id, 20)}</span>
                                <Typography variant="subtitle2" align="left">
                                    {item.name}
                                </Typography>
                            </IntegrationRowLeft>
                            {item.setupType === IntegrationSetupType.ContactSales ? (
                                <ContactSalesButton variant="outlined" />
                            ) : (
                                <Button variant="outlined" onClick={() => onSelect(item)}>
                                    {getActionText(item.setupType)}
                                </Button>
                            )}
                        </IntegrationRow>
                    ))}
                </IntegrationList>
            ) : (
                <EmptyResults>
                    <Typography variant="title1" sx={{ color: 'var(--text-primary)' }}>
                        {t('SettingsIntegrations.requestIntegrationTitle')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                        {t('SettingsIntegrations.requestIntegrationDescription')}
                    </Typography>
                    <ChipRow>
                        {term ? (
                            <ActionChip
                                clickable
                                variant="outlined"
                                icon={<SendRoundedIcon style={{ fontSize: 16 }} />}
                                label={t('SettingsIntegrations.requestNamed', { name: term })}
                                onClick={onSendRequest}
                            />
                        ) : null}
                        <ActionChip
                            clickable
                            variant="outlined"
                            icon={<img src={zapier} alt="" width={16} height={16} style={{ objectFit: 'contain' }} />}
                            label={t('SettingsIntegrations.connectViaZapier')}
                            onClick={() => onSelect({ setupType: IntegrationSetupType.Zapier })}
                        />
                        <ActionChip
                            clickable
                            variant="outlined"
                            icon={<ApiIcon width={16} height={16} />}
                            label={t('SettingsIntegrations.connectViaApi')}
                            onClick={() => onSelect({ setupType: IntegrationSetupType.ApiDocs })}
                        />
                    </ChipRow>
                </EmptyResults>
            )}
        </Modal>
    );
};

export default AddIntegrationModal;
